import React from 'react';
import { useTranslation } from 'react-i18next';
import { Send, Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { cn } from '../../../lib/utils';
import type { Heat } from '../../../types/heats';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Heats publiables (non publiés, avec participants). */
  filteredHeats: Heat[];
  uniqueWods: { id?: string; name?: string }[];
  uniqueCategories: { id?: string; name?: string }[];
  wodFilter: string;
  onWodFilterChange: (v: string) => void;
  categoryFilter: string;
  onCategoryFilterChange: (v: string) => void;
  selectedIds: string[];
  onToggleHeat: (heatId: string) => void;
  onToggleAll: () => void;
  /** Sélection ∩ heats affichés : ce qui sera réellement publié. */
  effectiveSelectedIds: string[];
  loading: boolean;
  onRequestPublish: () => void;
}

/**
 * Sélection des heats à publier.
 *
 * Publier envoie un email à chaque participant : c'est la seule action de
 * l'écran à conséquence extérieure, d'où cette sélection explicite plutôt
 * qu'un « tout publier » (des heats non publiés déclenchaient des emails
 * surprise avant la revue du 2026-07-03).
 */
const PublishHeatsDialog: React.FC<Props> = ({
  open, onOpenChange, filteredHeats, uniqueWods, uniqueCategories,
  wodFilter, onWodFilterChange, categoryFilter, onCategoryFilterChange,
  selectedIds, onToggleHeat, onToggleAll, effectiveSelectedIds, loading, onRequestPublish,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>
            {t('heats.publishDialog.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <p className="text-sm text-muted-foreground">{t('heats.publishDialog.description')}</p>

          {/* Filters */}
          <div className="flex gap-3 flex-wrap">
            {uniqueWods.length > 1 && (
              <Select value={wodFilter} onValueChange={onWodFilterChange}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder={t('heats.publishDialog.allWods')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('heats.publishDialog.allWods')}</SelectItem>
                  {uniqueWods.map(wod => (
                    <SelectItem key={wod.id} value={wod.id!}>{wod.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {uniqueCategories.length > 1 && (
              <Select value={categoryFilter} onValueChange={onCategoryFilterChange}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder={t('heats.publishDialog.allCategories')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('heats.publishDialog.allCategories')}</SelectItem>
                  {uniqueCategories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id!}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Table */}
          <div className="max-h-[400px] overflow-y-auto border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">
                    <Checkbox
                      checked={filteredHeats.length > 0 && filteredHeats.every(h => selectedIds.includes(h.id))}
                      onCheckedChange={onToggleAll}
                    />
                  </TableHead>
                  <TableHead>{t('heats.table.heat')}</TableHead>
                  <TableHead>{t('heats.table.wod')}</TableHead>
                  <TableHead>{t('heats.table.category')}</TableHead>
                  <TableHead>{t('heats.table.time')}</TableHead>
                  <TableHead className="text-right">{t('heats.table.participants')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredHeats.map((heat) => (
                  <TableRow
                    key={heat.id}
                    className={cn("cursor-pointer", selectedIds.includes(heat.id) && "bg-muted")}
                    onClick={() => onToggleHeat(heat.id)}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedIds.includes(heat.id)}
                        onCheckedChange={() => onToggleHeat(heat.id)}
                      />
                    </TableCell>
                    <TableCell className="font-medium">Heat {heat.heat_number}</TableCell>
                    <TableCell>{heat.wod_name || '-'}</TableCell>
                    <TableCell>{heat.category_name || '-'}</TableCell>
                    <TableCell>{heat.start_time?.substring(0, 5)}</TableCell>
                    <TableCell className="text-right">{heat.participant_count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Summary */}
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>{t('heats.publishDialog.selectedCount', { count: effectiveSelectedIds.length })}</span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={onRequestPublish} disabled={loading || effectiveSelectedIds.length === 0}>
            {loading ? <Loader2 size={18} className="animate-spin mr-2" /> : <Send size={18} className="mr-2" />}
            {t('heats.publishDialog.confirmPublish', { count: effectiveSelectedIds.length })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PublishHeatsDialog;
